import React, { useState, useEffect } from "react";
import "./css/PlayerModal.css";
import ReactDOM from "react-dom";
import config from "./config";

const devLog = (...args) => {
  if (config.IS_DEV) {
    console.log(...args);
  }
};
const PlayerModal = ({
  player = {},
  isOpen,
  onClose,
  playerStats = [], // Array of all players with their stats
  updatePlayerStat, // Function to update a player's stat
  currentAssignments = {}, // Mapping of position indices to player IDs
  onPositionAssignmentChange = () => {}, // Callback to update assignment
  players = [], // Full list of players
  positionIndex, // The position index for which this modal was opened
  setPlayerStats, // Function to update playerStats
  formationPositions = [], // Array of formation positions
}) => {
  // Local state to control whether to show the substitute list
  const [showSubList, setShowSubList] = useState(false);

  useEffect(() => {
    if (isOpen && player) {
      devLog("PlayerModal: Opened for player:", player);
    }
  }, [isOpen, player]);

  if (!isOpen || !player) return null;

  // Find the current player's stats using player_id
  const currentPlayerStats = playerStats.find(
    (p) => p.player_id === player.player_id
  ) || {
    goals: 0,
    assists: 0,
    shots_on_target: 0,
    shots_off_target: 0,
    defensive_actions: 0,
    saves: 0,
  };

  const handleIncrement = (statKey) => {
    updatePlayerStat(player.player_id, statKey, 1);
  };

  const handleDecrement = (statKey) => {
    updatePlayerStat(player.player_id, statKey, -1);
  };

  // Compute available substitutes: exclude the current player and any already assigned.
  const assignedPlayerIds = Object.values(currentAssignments).filter(Boolean);
  const availableSubs = players.filter((p) => p.player_id !== player.player_id);

  const handleSubstituteSelect = (subPlayer) => {
    devLog("PlayerModal: Substitute selected:", subPlayer);
    devLog("PlayerModal: Current positionIndex:", positionIndex);
    devLog("PlayerModal: FormationPositions:", formationPositions);

    // Guard against a null position index.
    if (positionIndex == null) {
      console.error("No position index set. Cannot substitute.");
      return;
    }

    // Determine the new formation position for the clicked position.
    const newFormationPosition = formationPositions[positionIndex] || "bench";
    devLog(
      "PlayerModal: New formation position for substitute:",
      newFormationPosition
    );

    // Convert currentAssignments to an object copy.
    const newAssignments = { ...currentAssignments };

    // Check if the chosen substitute is already assigned.
    const currentAssignmentsArray = Object.entries(currentAssignments);
    const existingAssignment = currentAssignmentsArray.find(
      ([, playerId]) => playerId === subPlayer.player_id
    );

    if (existingAssignment) {
      // The substitute is already assigned.
      const [subPosIndexStr] = existingAssignment;
      const subPosIndex = parseInt(subPosIndexStr, 10);
      devLog(
        "PlayerModal: Substitute is already playing at position index:",
        subPosIndex
      );

      // Swap the assignments: assign the substitute to the current (clicked) position
      // and assign the current (replaced) player to the substitute’s former position.
      newAssignments[positionIndex] = subPlayer.player_id;
      newAssignments[subPosIndex] = player.player_id;

      // Update playerStats to swap their formation positions
      setPlayerStats((prevStats) =>
        prevStats.map((p) => {
          if (p.player_id === subPlayer.player_id) {
            devLog(
              "PlayerModal: Updating substitute player's stats to new formation position:",
              newFormationPosition
            );
            return {
              ...p,
              status: "playing",
              position: newFormationPosition,
            };
          }
          if (p.player_id === player.player_id) {
            const subOldFormation = formationPositions[subPosIndex] || "bench";
            devLog(
              "PlayerModal: Updating replaced player's stats to sub's old formation position:",
              subOldFormation
            );
            return {
              ...p,
              status: "playing",
              position: subOldFormation,
            };
          }
          return p;
        })
      );
    } else {
      // The substitute is not currently assigned.
      newAssignments[positionIndex] = subPlayer.player_id;
      // Mark the substitute as playing and the replaced player as sub.
      setPlayerStats((prevStats) =>
        prevStats.map((p) => {
          if (p.player_id === subPlayer.player_id) {
            devLog(
              "PlayerModal: Updating substitute player's stats to new formation position:",
              newFormationPosition
            );
            return {
              ...p,
              status: "playing",
              position: newFormationPosition,
            };
          }
          if (p.player_id === player.player_id) {
            devLog("PlayerModal: Marking replaced player's stats as sub.");
            return {
              ...p,
              status: "sub",
              position: "bench",
            };
          }
          return p;
        })
      );
    }

    devLog("PlayerModal: New assignments object:", newAssignments);
    // Now update the assignment in one go.
    onPositionAssignmentChange(null, newAssignments);
    devLog("PlayerModal: Updated playerStats after substitution.");
    // Hide the substitute list and close the modal.
    setShowSubList(false);
    onClose();
  };

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{player.player_name || "Unnamed Player"}</h2>
        <p>Position: {player.position}</p>

        {/* Stats Controls */}
        <div className="stat-row">
          <span>Goals:</span>
          <button
            className="icon-button minus"
            onClick={() => handleDecrement("goals")}
          >
            -
          </button>
          <span>{currentPlayerStats.goals}</span>
          <button
            className="icon-button plus"
            onClick={() => handleIncrement("goals")}
          >
            +
          </button>
        </div>
        <div className="stat-row">
          <span>Assists:</span>
          <button
            className="icon-button minus"
            onClick={() => handleDecrement("assists")}
          >
            -
          </button>
          <span>{currentPlayerStats.assists}</span>
          <button
            className="icon-button plus"
            onClick={() => handleIncrement("assists")}
          >
            +
          </button>
        </div>
        <div className="stat-row">
          <span>Shots on Target:</span>
          <button
            className="icon-button minus"
            onClick={() => handleDecrement("shots_on_target")}
          >
            -
          </button>
          <span>{currentPlayerStats.shots_on_target}</span>
          <button
            className="icon-button plus"
            onClick={() => handleIncrement("shots_on_target")}
          >
            +
          </button>
        </div>
        <div className="stat-row">
          <span>Shots off Target:</span>
          <button
            className="icon-button minus"
            onClick={() => handleDecrement("shots_off_target")}
          >
            -
          </button>
          <span>{currentPlayerStats.shots_off_target}</span>
          <button
            className="icon-button plus"
            onClick={() => handleIncrement("shots_off_target")}
          >
            +
          </button>
        </div>
        <div className="stat-row">
          <span>Defensive Actions:</span>
          <button
            className="icon-button minus"
            onClick={() => handleDecrement("defensive_actions")}
          >
            -
          </button>
          <span>{currentPlayerStats.defensive_actions}</span>
          <button
            className="icon-button plus"
            onClick={() => handleIncrement("defensive_actions")}
          >
            +
          </button>
        </div>
        <div className="stat-row">
          <span>Saves:</span>
          <button
            className="icon-button minus"
            onClick={() => handleDecrement("saves")}
          >
            -
          </button>
          <span>{currentPlayerStats.saves}</span>
          <button
            className="icon-button plus"
            onClick={() => handleIncrement("saves")}
          >
            +
          </button>
        </div>

        <div className="sub-modal-actions">
          <button
            onClick={() => setShowSubList(true)}
            className="sub-modal-close-button"
          >
            Sub
          </button>
          <button onClick={onClose} className="sub-modal-close-button">
            Close
          </button>
        </div>

        {/* Substitute List */}
        {showSubList && (
          <div
            className="sub-modal-overlay"
            onClick={() => setShowSubList(false)}
          >
            <div
              className="sub-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="sub-modal-title">Select Substitute</h3>
              <p className="sub-modal-instruction">
                Choose a substitute from the list below:
              </p>

              <ul className="sub-modal-player-list">
                {availableSubs.map((sub) => {
                  const subPosition =
                    Object.entries(currentAssignments).find(
                      ([, playerId]) => playerId === sub.player_id
                    )?.[0] ?? "bench";

                  const isPlaying = subPosition !== "bench";

                  return (
                    <li key={sub.player_id}>
                      <button
                        className={`sub-modal-player-item ${
                          isPlaying ? "active-player" : "bench-player"
                        }`}
                        onClick={() => handleSubstituteSelect(sub)}
                      >
                        <span>
                          {sub.player_name}{" "}
                          <span className="player-position">
                            ({formationPositions[subPosition] || "bench"})
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
                {availableSubs.length === 0 && (
                  <li style={{ color: "#777", marginTop: "10px" }}>
                    No available substitutes.
                  </li>
                )}
              </ul>

              <div className="sub-modal-actions">
                <button
                  className="sub-modal-close-button"
                  onClick={() => setShowSubList(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
  return ReactDOM.createPortal(modalContent, document.body);
};

export default PlayerModal;
